import { ClobBook } from '@/types/clob.types';
import axiosClient from './axiosClient';

import { API_ENDPOINTS } from '@/constants/api.constants';

/**
 * CLOB Data API Service
 *
 * Specifically for public data from the Polymarket CLOB
 * that doesn't go through our backend.
 */
export const clobDataApi = {
  /**
   * Batch fetch order books for multiple token IDs.
   *
   * @param tokenIds - Array of token_id strings
   * @returns Array of order books with their respective token_ids
   */
  getBooks: async (tokenIds: string[]): Promise<ClobBook[]> => {
    if (!tokenIds.length) return [];

    const payload = tokenIds.map((id) => ({ token_id: id }));

    const { data } = await axiosClient.post<ClobBook[]>(API_ENDPOINTS.CLOB.BOOKS, payload);

    return data;
  },

  /**
   * Fetch user orders for a specific market.
   *
   * @param marketId - The market (condition ID or question ID)
   * @param nextCursor - Pagination cursor
   * @param headers - Authenticated headers (poly_address, poly_api_key, etc.)
   * @returns Order data from CLOB
   */
  getOrders: async (marketId: string, nextCursor: string = 'MA==', headers?: any): Promise<any> => {
    const { data } = await axiosClient.get(API_ENDPOINTS.CLOB.DATA.ORDERS, {
      params: {
        market: marketId,
        next_cursor: nextCursor,
        geo_block_token: '',
      },
      headers: headers || {},
    });
    return data;
  },

  /**
   * Fetch user trade history for a specific market.
   *
   * @param marketId - The market (condition ID or question ID)
   * @param nextCursor - Pagination cursor
   * @param headers - Authenticated headers (poly_address, poly_api_key, etc.)
   * @returns Trade data from CLOB
   */
  getTrades: async (marketId: string, nextCursor: string = 'MA==', headers?: any): Promise<any> => {
    const { data } = await axiosClient.get(API_ENDPOINTS.CLOB.DATA.TRADES, {
      params: {
        market: marketId,
        next_cursor: nextCursor,
        geo_block_token: '',
      },
      headers: headers || {},
    });
    return data;
  },

  /**
   * Fetch price history for a specific market asset.
   *
   * @param marketId - The CLOB token ID
   * @param interval - The time interval (e.g., '1d')
   * @param fidelity - The data fidelity (seconds)
   * @returns Historical price points
   */
  getPricesHistory: async (
    marketId: string,
    interval: string = '1d',
    fidelity: number = 60
  ): Promise<{ history: { t: number; p: number }[] }> => {
    const { data } = await axiosClient.get(API_ENDPOINTS.CLOB.PRICES_HISTORY, {
      params: {
        market: marketId,
        interval: interval.toLowerCase(),
        fidelity,
      },
    });
    return data;
  },
};
